import React from 'react';

/**
 * AIAssistantSchema Component
 * Combines multiple schemas optimized for AI assistants (ChatGPT, Perplexity, Claude)
 * and voice assistants (Siri, Google Assistant, Alexa)
 * 
 * This creates a comprehensive knowledge graph that AI can parse
 * 
 * NOTE: FAQPage is NOT included here - each page has its own FAQSchema component
 * to avoid duplicate FAQPage errors in Google Search Console
 * 
 * IMPORTANT: Renders inline so Google can see it on first crawl
 */
const AIAssistantSchema = () => {
  // Comprehensive schema for AI understanding - Organization only (no FAQ to avoid duplicates)
  const aiSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": "https://audioandco.com/#business",
    "name": "Audio & Co®",
    "legalName": "Audio & Co Ltd",
    "description": "Audio & Co is an independent media production studio working remotely with clients worldwide. We offer done-for-you podcast production, YouTube channel management, book publishing, audiobook production, and mobile app development services for small businesses and entrepreneurs.",
    "url": "https://audioandco.com",
    "telephone": "+44-7557-640039",
    "email": "hello@audioandco.com",
    "foundingDate": "2019",
    "founder": {
      "@type": "Person",
      "name": "Jo Day"
    },
    "areaServed": "Worldwide",
    "priceRange": "££-£££",
    "paymentAccepted": ["Credit Card", "Bank Transfer", "PayPal"],
    "currenciesAccepted": "GBP",
    "openingHours": "Mo-Fr 09:00-17:00",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Audio & Co Services",
      "itemListElement": [
        {
          "@type": "Service",
          "name": "Podcast Production",
          "description": "Done-for-you podcast production including strategy, editing, show notes, and distribution to all platforms. Launch in 14 days.",
          "provider": {"@id": "https://audioandco.com/#business"},
          "serviceType": "Podcast Production",
          "offers": {
            "@type": "Offer",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "350",
              "priceCurrency": "GBP",
              "minPrice": "350"
            }
          }
        },
        {
          "@type": "Service",
          "name": "Book Publishing",
          "description": "Complete self-publishing service for Amazon Kindle, paperback, and hardback. 100% Amazon approval guaranteed.",
          "provider": {"@id": "https://audioandco.com/#business"},
          "serviceType": "Publishing",
          "offers": {
            "@type": "Offer",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "195",
              "priceCurrency": "GBP",
              "minPrice": "195"
            }
          }
        },
        {
          "@type": "Service",
          "name": "Audiobook Production",
          "description": "Professional audiobook production with ACX approval guarantee. Distributed on Audible, Apple Books, and more.",
          "provider": {"@id": "https://audioandco.com/#business"},
          "serviceType": "Audiobook Production",
          "offers": {
            "@type": "Offer",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "1995",
              "priceCurrency": "GBP",
              "minPrice": "1995"
            }
          }
        },
        {
          "@type": "Service",
          "name": "Mobile App Development",
          "description": "Custom mobile app development for small businesses. PWA and native apps. Alternative to Kajabi, Kartra, GoHighLevel.",
          "provider": {"@id": "https://audioandco.com/#business"},
          "serviceType": "App Development",
          "offers": {
            "@type": "Offer",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "3995",
              "priceCurrency": "GBP",
              "minPrice": "3995"
            }
          }
        },
        {
          "@type": "Service",
          "name": "YouTube Channel Management",
          "description": "Full YouTube channel management including video editing, SEO optimization, thumbnails, and growth strategy.",
          "provider": {"@id": "https://audioandco.com/#business"},
          "serviceType": "Video Production",
          "offers": {
            "@type": "Offer",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "450",
              "priceCurrency": "GBP",
              "minPrice": "450"
            }
          }
        }
      ]
    },
    "knowsAbout": [
      "Podcast Production",
      "Podcast Editing",
      "Podcast Launch Strategy",
      "YouTube Channel Management",
      "Video Editing",
      "Book Publishing",
      "Self-Publishing",
      "Amazon KDP",
      "Audiobook Production",
      "ACX Publishing",
      "Mobile App Development",
      "PWA Development",
      "Website Development",
      "Content Marketing",
      "DMAIC Methodology",
      "Lean Six Sigma for Small Business"
    ]
  };

  return (
    <script
      type="application/ld+json"
      data-schema="ai-assistant"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(aiSchema) }}
    />
  );
};

export default AIAssistantSchema;
